interface EmailDelivery {
  email: string;
  challenge: string;
  expiresAt: number;
}

type EmailSigninStep =
  | { status: 'email' }
  | { status: 'code'; delivery: EmailDelivery }
  | { status: 'sending'; request: AbortController; delivery: EmailDelivery | null }
  | { status: 'verifying'; request: AbortController; delivery: EmailDelivery };

/** Only the active email operation can change the challenge or release busy UI. */
export class EmailSigninSession {
  private step: EmailSigninStep = { status: 'email' };

  constructor(private readonly changed: () => void) {}

  get sending(): boolean { return this.step.status === 'sending'; }
  get verifying(): boolean { return this.step.status === 'verifying'; }
  get delivery(): Readonly<EmailDelivery> | null { return this.step.status === 'email' ? null : this.step.delivery; }

  send(): AbortController | null {
    if (this.sending || this.verifying) return null;
    const request = new AbortController();
    this.step = { status: 'sending', request, delivery: this.delivery };
    this.changed();
    return request;
  }

  verify(): AbortController | null {
    if (this.step.status !== 'code' || !this.step.delivery.challenge) return null;
    const request = new AbortController();
    this.step = { status: 'verifying', request, delivery: this.step.delivery };
    this.changed();
    return request;
  }

  owns(request: AbortController): boolean {
    return !request.signal.aborted && (this.step.status === 'sending' || this.step.status === 'verifying') && this.step.request === request;
  }

  delivered(request: AbortController, email: string, challenge: string, expiresAt: number): boolean {
    if (!this.owns(request) || this.step.status !== 'sending') return false;
    this.step = { ...this.step, delivery: { email, challenge, expiresAt } };
    this.changed();
    return true;
  }

  finish(request: AbortController): boolean {
    if (!this.owns(request)) return false;
    const delivery = this.delivery;
    this.step = delivery ? { status: 'code', delivery } : { status: 'email' };
    this.changed();
    return true;
  }

  cancel(): void {
    if (this.step.status !== 'sending' && this.step.status !== 'verifying') return;
    this.step.request.abort();
    const delivery = this.delivery;
    this.step = delivery ? { status: 'code', delivery } : { status: 'email' };
    this.changed();
  }

  reset(): void {
    this.cancel();
    this.step = { status: 'email' };
    this.changed();
  }
}
